import {
  makeEsArticle,
  esSaintRelated,
  type EsArticleInput,
} from "./learnFactoryEs";
import type { LearnFAQ } from "@/pages/learn/learnShared";

const reviewBlock = {
  writtenBy: "Neuro Wellness TMS Centers of America",
  reviewedBy: "Daniel Pietras, MD",
  published: "November 3, 2026",
  lastReviewed: "November 3, 2026",
  lastUpdated: "November 3, 2026",
};

const faqs: LearnFAQ[] = [
  {
    question: "¿Qué pasa entre las sesiones de SAINT®?",
    answer:
      "Entre sesiones, los pacientes toman un descanso de 50 minutos. La mayoría lee, trabaja en una laptop, descansa o se relaja en la clínica. Los descansos son una parte estructurada del protocolo: permiten que el cerebro consolide los efectos de la estimulación. Los pacientes permanecen en la clínica durante todo el día.",
  },
  {
    question: "¿Puedo trabajar entre sesiones de SAINT®?",
    answer:
      "Sí. Muchos pacientes trabajan en una laptop durante los descansos de 50 minutos. Hay Wi-Fi disponible. Sin embargo, cada día incluye 10 sesiones que ocupan la mayor parte del día, así que planee en consecuencia. Algunos pacientes se toman la semana libre del trabajo por completo.",
  },
  {
    question: "¿Cuánto duran los descansos entre sesiones de SAINT®?",
    answer:
      "Los descansos son de 50 minutos entre cada sesión. Con 10 sesiones al día, esto significa que pasa la mayor parte de cada día en la clínica. Los descansos son parte del protocolo, no tiempo libre opcional.",
  },
];

export const esBetweenSessions = makeEsArticle({
  slug: "what-happens-between-saint-sessions",
  title: "¿Qué pasa entre las sesiones de SAINT® TMS? | Neuro Wellness",
  description:
    "Entre las sesiones de SAINT®, los pacientes descansan 50 minutos. Conozca qué hacer durante los descansos, qué esperar y cómo se estructura el día de tratamiento.",
  h1: "¿Qué pasa entre las sesiones de SAINT® TMS?",
  breadcrumbLabel: "Entre sesiones de SAINT®",
  shortAnswer:
    "Entre las sesiones de SAINT®, los pacientes toman un descanso de 50 minutos. Durante los descansos, la mayoría lee, trabaja en una laptop, descansa o se relaja en la clínica. Los descansos son una parte estructurada del protocolo: permiten que el cerebro consolide los efectos de cada sesión de estimulación antes de que comience la siguiente. Los pacientes permanecen en la clínica durante todo el día, ya que cada día incluye 10 sesiones con descansos de 50 minutos. La mayoría de los pacientes permanece despierta y alerta entre sesiones. Los descansos no son opcionales.",
  keyTakeaways: [
    "Los descansos de 50 minutos entre cada sesión son parte del protocolo.",
    "Los pacientes leen, trabajan, descansan o se relajan durante los descansos.",
    "Los descansos permiten que el cerebro consolide los efectos de la estimulación.",
    "Los pacientes permanecen en la clínica durante todo el día.",
    "Los descansos no son opcionales: están estructurados.",
  ],
  sections: [
    {
      heading: "El descanso de 50 minutos",
      paragraphs: [
        "SAINT® administra 10 sesiones al día, cada una de unos 10 minutos, separadas por descansos de 50 minutos. Los descansos son una parte estructurada del protocolo: no son tiempo libre opcional. La investigación sugiere que los descansos permiten que el cerebro consolide los efectos de cada sesión de estimulación, lo que puede contribuir a la respuesta acelerada que se observa en SAINT®.",
      ],
    },
    {
      heading: "Qué hacen los pacientes durante los descansos",
      paragraphs: [
        "Durante los descansos de 50 minutos, la mayoría de los pacientes:",
      ],
      list: [
        "Lee un libro o revista",
        "Trabaja en una laptop (hay Wi-Fi disponible)",
        "Descansa o cierra los ojos",
        "Escucha música o podcasts",
        "Toma un refrigerio o bebida",
        "Conversa con el equipo de tratamiento",
      ],
    },
    {
      heading: "El día completo de tratamiento",
      paragraphs: [
        "Un día típico de tratamiento con SAINT® incluye 10 sesiones con descansos de 50 minutos, ocupando la mayor parte del día. Los pacientes llegan por la mañana y se van a última hora de la tarde. El horario exacto varía según la clínica, pero planee pasar la mayor parte de cada día en la clínica durante 5 días consecutivos. El protocolo de 5 días totaliza 50 sesiones, y antes de comenzar se requiere una evaluación psiquiátrica y una resonancia fMRI, programadas con anticipación.",
      ],
    },
  ],
  faqs,
  sources: [
    {
      citation:
        "Cole EJ, et al. Stanford Neuromodulation Therapy (SNT): A Double-Blind Randomized Controlled Trial. Am J Psychiatry. 2022;179(2):132-141.",
      url: "https://pubmed.ncbi.nlm.nih.gov/35624717/",
    },
  ],
  relatedLinks: esSaintRelated,
  reviewBlock,
} satisfies EsArticleInput);
